import {
  CaretDown,
  CaretUp,
  ChatCircleText,
  CheckCircle,
  Clock,
  FileTextIcon,
  Question,
  TargetIcon,
  WarningCircle,
} from "@phosphor-icons/react";
import React, { useMemo, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import CustomText from "../../../../components/common/general/CustomText/CustomText";
import { ActivityType, AssessmentType, useGetAssessmentQuestionBankQuery } from "@axio/graphql/generated/graphql";
import { colors, radii, spacing } from "../../../../styles/design-system";
import { clampPercentage, formatPercentage } from "../../../../utils/analyticsUtils";
import { formatDurationFromSeconds } from "../../../../utils/timeUtils";
import InsightsCallout from "./InsightsCallout";
import InsightsMetricCard from "./InsightsMetricCard";
import ModuleInsightsObjectiveItem from "./ModuleInsightsObjectiveItem";
import QuestionPerformanceRow from "./QuestionPerformanceRow";
import QuestionResultRow from "./QuestionResultRow";
import { type ModuleActivityViewModel } from "./insightsTypes";

type ModuleInsightsActivityItemProps = {
  activity: ModuleActivityViewModel;
  activityIndex: number;
  defaultExpanded?: boolean;
  isByModule?: boolean;
};

const getActivityPill = (activity: ModuleActivityViewModel) => {
  if (activity.activityType === ActivityType.Assessment) {
    const label = activity.assessmentType === AssessmentType.Mcq ? "MCQ" : "SOCRATIC";

    return {
      label,
      icon: <FileTextIcon size={14} color={colors.purple[700]} weight="bold" />,
      backgroundColor: colors.purple[50],
      borderColor: colors.purple[200],
      textColor: colors.purple[700],
    };
  }

  if (activity.activityType === ActivityType.Aitutor) {
    return {
      label: "ILE",
      icon: <ChatCircleText size={14} color="rgb(114, 68, 187)" weight="regular" />,
      backgroundColor: "rgb(232, 221, 251)",
      borderColor: "rgb(232, 221, 251)",
      textColor: "rgb(114, 68, 187)",
    };
  }

  if (activity.activityType === ActivityType.Assignment) {
    return {
      label: "Assignment",
      icon: <FileTextIcon size={14} color={colors.grey[600]} weight="regular" />,
      backgroundColor: colors.grey[50],
      borderColor: colors.grey[200],
      textColor: colors.grey[600],
    };
  }

  if (activity.activityType === ActivityType.Project) {
    return {
      label: "Project",
      icon: <FileTextIcon size={14} color={colors.grey[600]} weight="regular" />,
      backgroundColor: colors.grey[50],
      borderColor: colors.grey[200],
      textColor: colors.grey[600],
    };
  }

  if (activity.activityType === ActivityType.AxioResource) {
    return {
      label: "Resource",
      icon: <FileTextIcon size={14} color={colors.grey[600]} weight="regular" />,
      backgroundColor: colors.grey[50],
      borderColor: colors.grey[200],
      textColor: colors.grey[600],
    };
  }

  return {
    label: "Review",
    icon: <FileTextIcon size={14} color={colors.grey[600]} weight="regular" />,
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    textColor: colors.grey[600],
  };
};

const ModuleInsightsActivityItem: React.FC<ModuleInsightsActivityItemProps> = ({
  activity,
  activityIndex,
  defaultExpanded = false,
  isByModule = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [isHovered, setIsHovered] = useState(false);
  const activityPill = getActivityPill(activity);
  const isAssessment = activity.activityType === ActivityType.Assessment;
  const isAiTutor = activity.activityType === ActivityType.Aitutor;
  const isMcqAssessment = isAssessment && activity.assessmentType === AssessmentType.Mcq;
  const isSocraticAssessment = isAssessment && activity.assessmentType === AssessmentType.SocraticDialog;
  const shouldLoadAssessmentQuestionBank = Boolean(activity.assessmentId);
  const { data: assessmentQuestionBankData, loading: assessmentQuestionBankLoading } =
    useGetAssessmentQuestionBankQuery({
      variables: { assessmentId: activity.assessmentId || "" },
      skip: !shouldLoadAssessmentQuestionBank || !expanded,
      fetchPolicy: "cache-first",
    });
  const assessmentQuestionBank = assessmentQuestionBankData?.getAssessmentQuestionBank?.data;
  const mcqQuestions = assessmentQuestionBank?.mcqQuestions ?? [];
  const socraticQuestions = useMemo(() => {
    const questionsById = new Map<string, { id: string; question?: string | null }>();

    (assessmentQuestionBank?.aiTutors ?? []).forEach((aiTutor) => {
      aiTutor.questions.forEach((question) => {
        if (!questionsById.has(question.id)) {
          questionsById.set(question.id, question);
        }
      });
    });

    return [...questionsById.values()];
  }, [assessmentQuestionBank]);

  const summaryLines = useMemo(
    () => activity.analytics?.summaries?.filter(Boolean).map(String) ?? [],
    [activity.analytics]
  );
  const successLines = useMemo(
    () => activity.analytics?.success_points_details?.filter(Boolean).map(String) ?? [],
    [activity.analytics]
  );
  const confusionLines = useMemo(
    () => activity.analytics?.confusion_points_details?.filter(Boolean).map(String) ?? [],
    [activity.analytics]
  );
  const recommendationLines = useMemo(
    () => activity.analytics?.further_recommendations?.filter(Boolean).map(String) ?? [],
    [activity.analytics]
  );
  const objectives = activity.analytics?.details.objectives ?? [];
  const successPoints = activity.analytics?.success_points ?? 0;
  const confusionPoints = activity.analytics?.confusion_points ?? 0;
  const activityStatus = activity?.status;

  const objectiveProgressDetails = useMemo(
    () => objectives.map((objective) => objective.progress_details).filter(Boolean),
    [objectives]
  );
  const originalityLine = useMemo(
    () =>
      objectiveProgressDetails.map((detail) => detail.originality_analysis).find((detail) => detail && detail.trim()),
    [objectiveProgressDetails]
  );
  const studentResponseTimes = objectiveProgressDetails.flatMap((detail) =>
    typeof detail.average_student_response_time === "number" ? [detail.average_student_response_time] : []
  );
  const aiResponseTimes = objectiveProgressDetails.flatMap((detail) =>
    typeof detail.average_ai_response_time === "number" ? [detail.average_ai_response_time] : []
  );
  const studentMessageCounts = objectiveProgressDetails.flatMap((detail) =>
    typeof detail.student_messages_count === "number" ? [detail.student_messages_count] : []
  );
  const aiMessageCounts = objectiveProgressDetails.flatMap((detail) =>
    typeof detail.ai_messages_count === "number" ? [detail.ai_messages_count] : []
  );
  const averageStudentResponseTime =
    studentResponseTimes.length > 0
      ? studentResponseTimes.reduce((sum, value) => sum + value, 0) / studentResponseTimes.length
      : null;
  const averageAiResponseTime =
    aiResponseTimes.length > 0 ? aiResponseTimes.reduce((sum, value) => sum + value, 0) / aiResponseTimes.length : null;
  const averageStudentMessages =
    studentMessageCounts.length > 0
      ? studentMessageCounts.reduce((sum, value) => sum + value, 0) / studentMessageCounts.length
      : null;
  const averageAiMessages =
    aiMessageCounts.length > 0 ? aiMessageCounts.reduce((sum, value) => sum + value, 0) / aiMessageCounts.length : null;
  const objectiveQuestionRows = useMemo(
    () =>
      objectives.map((objective) => ({
        sourceKey: objective.id,
        label: objective.label,
        correctPct:
          objective.success_points + objective.confusion_points > 0
            ? (objective.success_points / (objective.success_points + objective.confusion_points)) * 100
            : (activity.averageScorePct ?? 0),
        detail:
          objective.progress_details?.confusion_points?.find((detail) => typeof detail === "string" && detail.trim()) ??
          null,
      })),
    [activity.averageScorePct, objectives]
  );
  const assessmentQuestionRows = useMemo(() => {
    if (isMcqAssessment && mcqQuestions.length > 0) {
      return mcqQuestions.map((question, index) => ({
        sourceKey: question.id,
        label: question.question,
        correctPct: objectiveQuestionRows[index]?.correctPct ?? activity.averageScorePct ?? 0,
        detail: objectiveQuestionRows[index]?.detail ?? question.explanation ?? null,
      }));
    }

    if (isSocraticAssessment && socraticQuestions.length > 0) {
      return socraticQuestions.map((question, index) => ({
        sourceKey: question.id,
        label: question.question ?? `Question ${index + 1}`,
        correctPct: objectiveQuestionRows[index]?.correctPct ?? activity.averageScorePct ?? 0,
        detail: objectiveQuestionRows[index]?.detail ?? null,
      }));
    }

    return objectiveQuestionRows;
  }, [
    activity.averageScorePct,
    isMcqAssessment,
    isSocraticAssessment,
    mcqQuestions,
    objectiveQuestionRows,
    socraticQuestions,
  ]);
  const averageTimePerQuestionSeconds = useMemo(() => {
    if (typeof activity.assessmentSegment?.averageTimePerQuestionSeconds === "number") {
      return activity.assessmentSegment.averageTimePerQuestionSeconds;
    }

    if (typeof activity.averageTimeSeconds === "number" && activity.questionsCount > 0) {
      return activity.averageTimeSeconds / activity.questionsCount;
    }

    return null;
  }, [activity.assessmentSegment?.averageTimePerQuestionSeconds, activity.averageTimeSeconds, activity.questionsCount]);
  const averageTimeText =
    typeof activity.averageTimeSeconds === "number" ? formatDurationFromSeconds(activity.averageTimeSeconds) : "—";
  const averageTimePerQuestionText =
    typeof averageTimePerQuestionSeconds === "number" ? formatDurationFromSeconds(averageTimePerQuestionSeconds) : "—";
  const averageStudentResponseText =
    typeof averageStudentResponseTime === "number" ? formatDurationFromSeconds(averageStudentResponseTime) : "—";
  const averageAiResponseText =
    typeof averageAiResponseTime === "number" ? formatDurationFromSeconds(averageAiResponseTime) : "—";

  let completionText = "—";
  let completionWidth = 0;

  if (typeof activity.completionPct === "number") {
    completionText = formatPercentage(activity.completionPct);
    completionWidth = clampPercentage(activity.completionPct);
  }

  let averageScoreText = "—";

  if (typeof activity.averageScorePct === "number") {
    averageScoreText = formatPercentage(activity.averageScorePct);
  }

  return (
    <View style={styles.container}>
      <Pressable
        accessible={false}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onPress={() => setExpanded((prev) => !prev)}
        style={[styles.header, isHovered && styles.headerHovered]}>
        <View style={styles.headerText}>
          <View style={styles.activityIdentityRow}>
            <CustomText text={`Activity ${activityIndex}`} size="s" weight="medium" useTranslationText={false} />
            <View
              style={[
                styles.variantPill,
                { backgroundColor: activityPill.backgroundColor, borderColor: activityPill.borderColor },
              ]}>
              {activityPill.icon}
              <CustomText
                text={activityPill.label}
                size="xs"
                weight="bold"
                useTranslationText={false}
                style={[styles.variantPillText, { color: activityPill.textColor }]}
              />
            </View>

            {activityStatus ? (
              <View
                style={[
                  styles.statusPill,
                  {
                    borderColor: activityStatus === "Complete" ? colors.success[400] : colors.warning[400],
                    backgroundColor: activityStatus === "Complete" ? colors.success[50] : colors.warning[50],
                  },
                ]}>
                <CustomText
                  text={activityStatus}
                  size="xs"
                  useTranslationText={false}
                  style={{
                    color: activityStatus === "Complete" ? colors.success[700] : colors.warning[700],
                  }}
                />
              </View>
            ) : null}
          </View>
          <CustomText text={activity.title} size="m" weight="bold" useTranslationText={false} />
          <View style={styles.metaRow}>
            <View style={styles.metaItem}>
              <Clock size={12} color={colors.grey[500]} />
              <CustomText text={averageTimeText} size="xs" useTranslationText={false} style={styles.mutedText} />
            </View>
            <View style={styles.metaItem}>
              <View style={[styles.dot, { backgroundColor: colors.success[700] }]} />
              <CustomText
                text={`${completionText} completion`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <View style={[styles.dot, { backgroundColor: colors.warning[700] }]} />
              <CustomText
                text={`Avg score: ${averageScoreText}`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.metaItem}>
              <Question size={12} color={colors.grey[500]} />
              <CustomText
                text={`${activity.questionsCount} questions`}
                size="xs"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
          </View>
          {isAiTutor ? (
            <View style={styles.quickStatsRow}>
              <View style={styles.quickStat}>
                <CheckCircle size={12} color={colors.success[700]} />
                <CustomText
                  text={`${successPoints} success`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.mutedText}
                />
              </View>
              <View style={styles.quickStat}>
                <WarningCircle size={12} color={colors.error[700]} />
                <CustomText
                  text={`${confusionPoints} confusion`}
                  size="xs"
                  useTranslationText={false}
                  style={[styles.mutedText, confusionPoints >= 5 ? styles.errorText : null]}
                />
              </View>
              <View style={styles.quickStat}>
                <ChatCircleText size={12} color={colors.grey[500]} />
                <CustomText
                  text={`${activity.questionsCount} avg questions`}
                  size="xs"
                  useTranslationText={false}
                  style={styles.mutedText}
                />
              </View>
            </View>
          ) : null}
        </View>
        {expanded ? <CaretUp size={16} color={colors.grey[500]} /> : <CaretDown size={16} color={colors.grey[500]} />}
      </Pressable>

      {expanded ? (
        <View style={styles.content}>
          <InsightsCallout title={isAssessment ? "Assessment Summary" : "Summary"} lines={summaryLines} tone="brand" />

          <View style={styles.metricGrid}>
            <InsightsMetricCard label="Avg Score" value={averageScoreText} />
            <InsightsMetricCard label="Avg Time Spent" value={averageTimeText} />
            {isAssessment && <InsightsMetricCard label="Avg Time / Question" value={averageTimePerQuestionText} />}
            {isAiTutor && (
              <>
                <InsightsMetricCard label="Avg Student Response" value={averageStudentResponseText} />
                <InsightsMetricCard label="Avg AI Response" value={averageAiResponseText} />
                <InsightsMetricCard
                  label="Avg Student Messages"
                  value={typeof averageStudentMessages === "number" ? averageStudentMessages.toFixed(1) : "—"}
                />
                <InsightsMetricCard
                  label="Avg AI Messages"
                  value={typeof averageAiMessages === "number" ? averageAiMessages.toFixed(1) : "—"}
                />
              </>
            )}
          </View>

          {originalityLine ? (
            <InsightsCallout title="Class Originality" lines={[originalityLine]} tone="neutral" />
          ) : null}

          {isAssessment && assessmentQuestionBankLoading ? (
            <CustomText
              text="Loading assessment question bank..."
              size="xs"
              useTranslationText={false}
              style={styles.mutedText}
            />
          ) : null}

          {isAssessment && isByModule && (activity.assessmentSegment as any)?.questionResults?.length > 0 ? (
            <View style={styles.section}>
              <CustomText
                text={`Question Results (${(activity.assessmentSegment as any).questionResults.length})`}
                size="s"
                weight="bold"
                useTranslationText={false}
              />
              <View style={styles.objectivesList}>
                {(activity.assessmentSegment as any).questionResults.map((result: any, idx: number) => (
                  <QuestionResultRow key={result.questionId || idx} result={result} />
                ))}
              </View>
            </View>
          ) : isAssessment && !isByModule && assessmentQuestionRows.length > 0 ? (
            <View style={styles.section}>
              <CustomText text="Question Performance" size="s" weight="bold" useTranslationText={false} />
              <View style={styles.objectivesList}>
                {assessmentQuestionRows.map((row) => (
                  <QuestionPerformanceRow
                    key={row.sourceKey}
                    label={row.label}
                    correctPct={row.correctPct}
                    detail={row.detail}
                  />
                ))}
              </View>
            </View>
          ) : null}

          {isAiTutor && objectives.length > 0 ? (
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <TargetIcon size={18} color={colors.grey[500]} weight="regular" />
                <CustomText
                  text={`Learning Objectives (${objectives.length})`}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                />
              </View>
              <View style={styles.objectivesList}>
                {objectives.map((objective, index) => (
                  <ModuleInsightsObjectiveItem
                    key={objective.id}
                    objective={objective}
                    objectiveIndex={index + 1}
                    defaultExpanded={index === 0}
                  />
                ))}
              </View>
            </View>
          ) : null}

          {isAiTutor ? <InsightsCallout title="What Went Well" lines={successLines} tone="success" /> : null}
          {isAiTutor ? <InsightsCallout title="Areas of Confusion" lines={confusionLines} tone="warning" /> : null}
          <InsightsCallout title="AI Recommendations" lines={recommendationLines} tone="brand" />

          <View style={styles.completionFooter}>
            <View style={styles.completionHeader}>
              <CustomText
                text="Completion"
                size="xs"
                weight="medium"
                useTranslationText={false}
                style={styles.mutedText}
              />
              <CustomText
                text={completionText}
                size="xs"
                weight="bold"
                useTranslationText={false}
                style={styles.mutedText}
              />
            </View>
            <View style={styles.track}>
              <View style={[styles.fill, { width: `${completionWidth}%`, backgroundColor: colors.success[500] }]} />
            </View>
          </View>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 10,
    backgroundColor: colors.white,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  headerHovered: {
    backgroundColor: colors.grey[100],
  },
  headerText: {
    flex: 1,
    gap: 4,
  },
  activityIdentityRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  variantPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1,
    borderRadius: radii.xs,
    paddingHorizontal: spacing.xs,
    paddingVertical: 1,
  },
  variantPillText: {
    color: colors.brand[700],
  },
  statusPill: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: radii.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  quickStatsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
    marginTop: 2,
  },
  quickStat: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 999,
  },
  mutedText: {
    color: colors.grey[500],
  },
  errorText: {
    color: colors.error[700],
  },
  content: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    padding: spacing.sm,
    gap: spacing.sm,
    backgroundColor: colors.grey[50],
  },
  metricGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.xs,
  },
  section: {
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 10,
    padding: spacing.sm,
    gap: spacing.xs,
    backgroundColor: colors.white,
  },
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  objectivesList: {
    gap: spacing.xs,
  },
  completionFooter: {
    marginTop: spacing.sm,
    gap: 4,
  },
  completionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  track: {
    width: "100%",
    height: 6,
    borderRadius: radii.full,
    backgroundColor: colors.grey[200],
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    borderRadius: radii.full,
  },
});

export default ModuleInsightsActivityItem;
